import { CalendarOff, Trash2 } from 'lucide-react';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import {
  ClosurePeriodsField,
  type InheritedClosuresType,
} from '@/components/Admin/ClosurePeriodsField';
import {
  closureReasonMaxLength,
  sortClosurePeriods,
  weekdayLabels,
  weekdayOrder,
  weekdayPluralLabel,
  weekdayShortLabels,
  type ClosurePeriodType,
  type WeekdayClosureType,
} from '@/types/types_closure';

interface AvailabilityClosuresFieldProps {
  periods: ClosurePeriodType[];
  weekdays: WeekdayClosureType[];
  onChangePeriods: (periods: ClosurePeriodType[]) => void;
  onChangeWeekdays: (weekdays: WeekdayClosureType[]) => void;
  /** Today, `yyyy-mm-dd`. Only used to mark absences that are already over. */
  today: string;
  /** The supplier's absences, which this experience is closed for as well. */
  inherited?: InheritedClosuresType;
}

/**
 * The days this experience cannot be run on: the artisan's absences, and the
 * days of the week it never runs.
 *
 * Both are fields on the experience rather than documents of their own, so this
 * is a controlled component like `GalleryImagesField` — the page commits each
 * change, which is what puts closures on the autosave, history and undo stack
 * along with everything else on the form. Deleting one is therefore undoable,
 * which is why it is not confirmed.
 *
 * The absence half is `ClosurePeriodsField`, shared with the supplier form. Rows
 * are keyed by their span, which identifies them because `validateClosurePeriod`
 * refuses two absences sharing both ends.
 */
export function AvailabilityClosuresField({
  periods,
  weekdays,
  onChangePeriods,
  onChangeWeekdays,
  today,
  inherited,
}: AvailabilityClosuresFieldProps) {
  // Two absences cannot share both ends (`validateClosurePeriod` refuses it), so
  // the span identifies the row — which is the key the shared editor addresses
  // it by, in place of the map ids a supplier's absences have.
  const rows = sortClosurePeriods(periods).map((period) => ({ key: rowKey(period), period }));

  function rowKey(period: ClosurePeriodType): string {
    return `${period.startDate}_${period.endDate}`;
  }

  function toggleWeekday(weekday: number) {
    const already = weekdays.some((c) => c.weekday === weekday);
    onChangeWeekdays(
      already
        ? weekdays.filter((c) => c.weekday !== weekday)
        : [...weekdays, { weekday, reason: '' }],
    );
  }

  function updateWeekdayReason(weekday: number, value: string) {
    onChangeWeekdays(weekdays.map((c) => (c.weekday === weekday ? { ...c, reason: value } : c)));
  }

  const closedWeekdays = weekdayOrder.filter((day) => weekdays.some((c) => c.weekday === day));

  return (
    <div className='tw:space-y-6'>
      <ClosurePeriodsField
        rows={rows}
        today={today}
        hint='A span of dates this experience cannot be run on. Both days are included.'
        emptyMessage='No absences recorded. Every future date is offered to partners.'
        inherited={inherited}
        onAdd={(period) => onChangePeriods(sortClosurePeriods([...periods, period]))}
        onChangeReason={(key, value) =>
          onChangePeriods(periods.map((p) => (rowKey(p) === key ? { ...p, reason: value } : p)))}
        onRemove={(key) => onChangePeriods(periods.filter((p) => rowKey(p) !== key))}
      />

      {/* --- Weekly closures --- */}
      <div className='tw:space-y-3'>
        <div>
          <p className='tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
            Closed every week on
          </p>
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            For a day the experience never runs — the market it visits is shut, or the artisan does not
            work it. Applies to every week, with no end date.
          </p>
        </div>

        <div className='tw:flex tw:flex-wrap tw:gap-2'>
          {weekdayOrder.map((day) => {
            const closed = weekdays.some((c) => c.weekday === day);
            return (
              <button
                key={day}
                type='button'
                aria-pressed={closed}
                onClick={() => toggleWeekday(day)}
                className={`tw:rounded-md tw:border tw:px-3 tw:py-1.5 tw:text-sm tw:transition-colors ${
                  closed
                    ? 'tw:border-ed-gold tw:bg-ed-gold/15 tw:text-foreground'
                    : 'tw:text-muted-foreground tw:hover:border-ed-gold'
                }`}
              >
                {closed && <CalendarOff className='tw:mr-1.5 tw:inline tw:size-3.5' />}
                {weekdayShortLabels[day]}
              </button>
            );
          })}
        </div>

        {closedWeekdays.length > 0 && (
          <ul className='tw:divide-y tw:rounded-md tw:border'>
            {closedWeekdays.map((day) => (
              <li key={day} className='tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:text-sm'>
                <span className='tw:w-72 tw:shrink-0'>Every {weekdayLabels[day]}</span>
                <div className='tw:min-w-48 tw:flex-1'>
                  <AutosaveTextInput
                    currentValue={weekdays.find((c) => c.weekday === day)?.reason ?? ''}
                    placeholder='Reason (optional)'
                    onCommitChange={(v) => updateWeekdayReason(day, v.slice(0, closureReasonMaxLength))}
                  />
                </div>
                <button
                  type='button'
                  onClick={() => toggleWeekday(day)}
                  aria-label={`Stop closing ${weekdayPluralLabel(day)}`}
                  className='tw:text-muted-foreground tw:transition-colors tw:hover:text-destructive'
                >
                  <Trash2 className='tw:size-4' />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className='tw:text-xs tw:text-muted-foreground'>
        Closed days are struck through in the date picker on the experience page, with the reason beside
        them, and a request naming one is refused before it reaches us.
      </p>
    </div>
  );
}
